import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ExternalLink, Eye, Plus, Search, Trash2 } from "lucide-react";
import { api, apiError, asset, formatDate } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import PageVisualEditor from "@/components/admin/PageVisualEditor";
import MediaPicker, { canonicalMediaPath, normalizeMediaRefs } from "@/components/admin/MediaPicker";
import { DEFAULT_TEAM_PAGE } from "@/lib/teamDefaults";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  AdminTable,
  EmptyState,
  FilterTabs,
  StatusBadge,
  adminInputClass,
} from "@/components/admin/AdminUi";

const KIND_META = {
  pages: {
    title: "Website Pages",
    subtitle: "Manage your website content and structure.",
    addLabel: "Add New Page",
    columns: ["Page Title", "URL / Slug", "Last Updated", "Status", "Actions"],
    publicPath: (item) => (item.slug === "home" ? "/" : `/${item.slug}`),
  },
  posts: {
    title: "Blog / News",
    subtitle: "Publish kidney health education and community news.",
    addLabel: "Add New Post",
    columns: ["Title", "Category", "Published", "Status", "Actions"],
    publicPath: (item) => `/blog/${item.slug}`,
  },
  programs: {
    title: "Programs / Our Efforts",
    subtitle: "Manage the association's programs and initiatives.",
    addLabel: "Add Program",
    columns: ["Program", "URL / Slug", "Order", "Status", "Actions"],
    publicPath: (item) => `/programs/${item.slug}`,
  },
};

const TABS = [
  { value: "all", label: "All" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Drafts" },
  { value: "archived", label: "Archived" },
];

export function ContentList({ kind }) {
  const meta = KIND_META[kind];
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [installingTeam, setInstallingTeam] = useState(false);

  const load = useCallback(() => {
    setItems(null);
    api
      .get(`/admin/content/${kind}`, { params: { status, q: q || undefined } })
      .then((r) => setItems(r.data))
      .catch((e) => {
        setItems([]);
        setBanner({ status: "error", message: apiError(e) });
      });
  }, [kind, status, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const installTeamPage = async () => {
    setInstallingTeam(true);
    setBanner({ status: "idle", message: "" });
    try {
      await api.post("/admin/content/pages", DEFAULT_TEAM_PAGE);
      setBanner({ status: "ok", message: "Board of Directors page created and published. You can edit it like any other page." });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
    setInstallingTeam(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/content/${kind}/${item.id}`);
      setBanner({ status: "ok", message: `"${item.title}" was deleted.` });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const counts = items
    ? {
        all: items.length,
        published: items.filter((i) => i.status === "published").length,
        draft: items.filter((i) => i.status === "draft").length,
        archived: items.filter((i) => i.status === "archived").length,
      }
    : {};

  return (
    <div data-testid={`cms-${kind}-list`}>
      <Seo title={`${meta.title} | Bahamas Kidney Association CMS`} noindex />
      <AdminPageHead title={meta.title} subtitle={meta.subtitle}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <label htmlFor={`${kind}-search`} className="sr-only">
            Search {meta.title}
          </label>
          <input
            id={`${kind}-search`}
            data-testid={`cms-${kind}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className={`${adminInputClass} w-48 pl-9`}
          />
        </div>
        {kind === "pages" && can("content.write") && items && !items.some((i) => i.slug === "team") && (
          <AdminButton
            onClick={installTeamPage}
            disabled={installingTeam}
            testId="cms-pages-install-team"
          >
            <Plus className="h-4 w-4" /> {installingTeam ? "Creating…" : "Add Board Page"}
          </AdminButton>
        )}
        {can("content.write") && (
          <Link to={`/admin/${kind}/new`} data-testid={`cms-${kind}-add`}>
            <AdminButton variant="emerald">
              <Plus className="h-4 w-4" /> {meta.addLabel}
            </AdminButton>
          </Link>
        )}
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <FilterTabs
        tabs={TABS}
        value={status}
        onChange={setStatus}
        counts={status === "all" ? counts : {}}
        testIdPrefix={`cms-${kind}-filter`}
      />

      <AdminCard className="overflow-hidden">
        {items === null ? (
          <AdminSpinner />
        ) : items.length === 0 ? (
          <EmptyState message={`No ${kind} found.`} testId={`cms-${kind}-empty`} />
        ) : (
          <AdminTable columns={meta.columns} testId={`cms-${kind}-table`}>
            {items.map((item) => (
              <tr key={item.id} className="transition-colors hover:bg-slate-50/70" data-testid={`cms-${kind}-row-${item.slug}`}>
                <td className="px-5 py-4">
                  <Link
                    to={`/admin/${kind}/${item.id}`}
                    className="font-bold text-bka-navy hover:text-bka-emerald"
                    data-testid={`cms-${kind}-edit-${item.slug}`}
                  >
                    {item.title}
                  </Link>
                </td>
                <td className="px-5 py-4 text-slate-500">
                  {kind === "posts" ? item.category || "—" : meta.publicPath(item)}
                </td>
                <td className="px-5 py-4 text-slate-500">
                  {kind === "programs" ? item.sort_order ?? 0 : formatDate(item.published_at || item.updated_at)}
                </td>
                <td className="px-5 py-4">
                  <StatusBadge status={item.status} testId={`cms-${kind}-status-${item.slug}`} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1.5">
                    <a
                      href={asset(meta.publicPath(item))}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Preview ${item.title}`}
                      data-testid={`cms-${kind}-preview-${item.slug}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </a>
                    {can("content.write") && (
                      <button
                        type="button"
                        onClick={() => remove(item)}
                        aria-label={`Delete ${item.title}`}
                        data-testid={`cms-${kind}-delete-${item.slug}`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminCard>
    </div>
  );
}

const BLANK = {
  title: "",
  slug: "",
  status: "draft",
  excerpt: "",
  summary: "",
  body: "",
  category: "",
  author: "",
  hero_image_url: "",
  image_url: "",
  seo_title: "",
  seo_description: "",
  sort_order: 0,
};

export function ContentEditor({ kind }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const meta = KIND_META[kind];
  const [form, setForm] = useState(isNew ? BLANK : null);
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const [saving, setSaving] = useState(false);
  const [bodyJson, setBodyJson] = useState(
    kind === "pages" && isNew ? { hero: { overline: "", heading_line_1: "", heading_line_2: "", subheading: "" }, sections: [] } : null
  );

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/admin/content/${kind}/${id}`)
      .then((r) => {
        setForm({ ...BLANK, ...r.data });
        setBodyJson(r.data.body_json || null);
      })
      .catch((e) => setBanner({ status: "error", message: apiError(e) }));
  }, [kind, id, isNew]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (overrideStatus) => {
    setSaving(true);
    setBanner({ status: "idle", message: "" });
    const payload = {
      ...form,
      hero_image_url: canonicalMediaPath(form.hero_image_url),
      image_url: canonicalMediaPath(form.image_url),
      status: overrideStatus || form.status,
      sort_order: Number(form.sort_order) || 0,
      body_json: bodyJson ? normalizeMediaRefs(bodyJson) : undefined,
    };
    try {
      const res = isNew
        ? await api.post(`/admin/content/${kind}`, payload)
        : await api.put(`/admin/content/${kind}/${id}`, payload);
      setBanner({ status: "ok", message: `Saved. "${res.data.title}" is now ${res.data.status}.` });
      setForm({ ...BLANK, ...res.data });
      if (isNew) navigate(`/admin/${kind}/${res.data.id}`, { replace: true });
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
    setSaving(false);
  };

  if (form === null) return <AdminSpinner label="Loading content" />;

  const publicPath = form.slug ? meta.publicPath(form) : null;

  return (
    <div data-testid={`cms-${kind}-editor`}>
      <Seo title={`${isNew ? "New" : "Edit"} ${meta.title} | Bahamas Kidney Association CMS`} noindex />
      <AdminPageHead
        title={isNew ? meta.addLabel : form.title || "Untitled"}
        subtitle={isNew ? "Create new content. It stays a draft until you publish it." : `Editing ${meta.title.toLowerCase()}`}
      >
        <Link to={`/admin/${kind}`} data-testid={`cms-${kind}-editor-back`}>
          <AdminButton variant="ghost">Back to list</AdminButton>
        </Link>
        {!isNew && publicPath && (
          <a href={asset(publicPath)} target="_blank" rel="noreferrer" data-testid={`cms-${kind}-editor-preview`}>
            <AdminButton variant="ghost">
              <ExternalLink className="h-4 w-4" /> Preview
            </AdminButton>
          </a>
        )}
        <AdminButton testId={`cms-${kind}-save-draft`} onClick={() => save("draft")} disabled={saving}>
          Save Draft
        </AdminButton>
        <AdminButton
          variant="emerald"
          testId={`cms-${kind}-publish`}
          onClick={() => save("published")}
          disabled={saving}
        >
          {saving ? "Saving…" : "Publish"}
        </AdminButton>
      </AdminPageHead>

      <AdminBanner
        status={banner.status}
        message={banner.message}
        onDismiss={() => setBanner({ status: "idle", message: "" })}
      />

      <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <AdminCard className="space-y-5 p-6">
          <AdminField label="Title" htmlFor="ce-title" required>
            <input id="ce-title" data-testid={`cms-${kind}-title`} value={form.title} onChange={set("title")} className={adminInputClass} placeholder="Enter a title" />
          </AdminField>

          <AdminField label="URL Slug" htmlFor="ce-slug" hint="Leave blank to generate it from the title.">
            <input id="ce-slug" data-testid={`cms-${kind}-slug`} value={form.slug} onChange={set("slug")} className={adminInputClass} placeholder="auto-generated-from-title" />
          </AdminField>

          {kind === "posts" && (
            <>
              <AdminField label="Category" htmlFor="ce-category">
                <input id="ce-category" data-testid="cms-posts-category" value={form.category} onChange={set("category")} className={adminInputClass} placeholder="e.g. Kidney Health" />
              </AdminField>
              <AdminField label="Excerpt" htmlFor="ce-excerpt" hint="Shown on listing cards and used for SEO.">
                <textarea id="ce-excerpt" data-testid="cms-posts-excerpt" rows={3} value={form.excerpt} onChange={set("excerpt")} className={adminInputClass} />
              </AdminField>
              <AdminField label="Author / Byline" htmlFor="ce-author">
                <input id="ce-author" data-testid="cms-posts-author" value={form.author} onChange={set("author")} className={adminInputClass} placeholder="Bahamas Kidney Association" />
              </AdminField>
            </>
          )}

          {kind === "programs" && (
            <>
              <AdminField label="Summary" htmlFor="ce-summary" hint="One or two lines shown on the programs grid.">
                <textarea id="ce-summary" data-testid="cms-programs-summary" rows={3} value={form.summary} onChange={set("summary")} className={adminInputClass} />
              </AdminField>
              <AdminField label="Display Order" htmlFor="ce-order">
                <input id="ce-order" data-testid="cms-programs-order" type="number" value={form.sort_order} onChange={set("sort_order")} className={adminInputClass} />
              </AdminField>
            </>
          )}

          {kind !== "pages" && (
            <AdminField
              label="Body"
              htmlFor="ce-body"
              hint="Separate paragraphs with a blank line. Use **bold** for emphasis."
            >
              <textarea id="ce-body" data-testid={`cms-${kind}-body`} rows={16} value={form.body} onChange={set("body")} className={`${adminInputClass} font-mono text-[13px] leading-relaxed`} />
            </AdminField>
          )}

          {kind === "pages" && (
            <PageVisualEditor
              value={bodyJson || { hero: { overline: "", heading_line_1: "", heading_line_2: "", subheading: "" }, sections: [] }}
              onChange={setBodyJson}
            />
          )}
        </AdminCard>

        <div className="space-y-5">
          <AdminCard className="space-y-5 p-6">
            <div>
              <p className="mb-1.5 text-sm font-bold text-bka-navy">Status</p>
              <StatusBadge status={form.status} testId={`cms-${kind}-current-status`} />
            </div>
            <MediaPicker
              kind="images"
              label={kind === "pages" ? "Main Page Picture" : "Featured Picture"}
              value={kind === "pages" ? form.hero_image_url : form.image_url}
              onChange={(url) =>
                setForm((f) => ({
                  ...f,
                  [kind === "pages" ? "hero_image_url" : "image_url"]: url,
                }))
              }
              buttonLabel={kind === "pages" ? "Choose or Upload Main Picture" : "Choose or Upload Featured Picture"}
              testId={`cms-${kind}-image-picker`}
            />
            <p className="text-xs leading-relaxed text-slate-500">
              Click the picture box, then upload a new picture or click one already in the library.
              The CMS attaches it automatically.
            </p>
          </AdminCard>

          <AdminCard className="space-y-5 p-6">
            <h2 className="font-display text-base font-bold text-bka-navy">SEO</h2>
            <AdminField label="SEO Title" htmlFor="ce-seo-title">
              <input id="ce-seo-title" data-testid={`cms-${kind}-seo-title`} value={form.seo_title} onChange={set("seo_title")} className={adminInputClass} />
            </AdminField>
            <AdminField label="Meta Description" htmlFor="ce-seo-desc" hint="Around 150–160 characters.">
              <textarea id="ce-seo-desc" data-testid={`cms-${kind}-seo-description`} rows={3} value={form.seo_description} onChange={set("seo_description")} className={adminInputClass} />
            </AdminField>
          </AdminCard>

          {form.updated_by_email && (
            <AdminCard className="p-6 text-xs text-slate-500">
              Last updated by <strong className="text-bka-navy">{form.updated_by_email}</strong> on{" "}
              {formatDate(form.updated_at)}
            </AdminCard>
          )}
        </div>
      </div>
    </div>
  );
}
